interface WistiaOptions {
    videoFoam: boolean;
    playerColor: string;
    controlsVisibleOnLoad: boolean;
    googleAnalytics: boolean;
    fitStrategy: string;
    preload: string;
    wmode: string;
    playButton?: boolean;
    popover?: boolean;
}

interface WistiaVideo {
    options: WistiaOptions;
    container: HTMLElement;
    play(): void;
    bind(event: string, callback: () => void): void;
    popover: {
        show(): void;
    };
}

interface WistiaQueueItem {
    id: string;
    options: WistiaOptions;
    onReady(video: WistiaVideo): void;
}

declare global {
    interface Window {
        _wq: WistiaQueueItem[];
    }
}
export default function videoEmbed(els) {
    els.forEach(wistiaPlaceholder => {
        const observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const wistiaSnippet =
                            wistiaPlaceholder.getAttribute('data-wistia-snippet'); // Get the Wistia snippet inlined inside the data attribute
                        const wistiaContainer = document.createElement('div'); // Create a placeholder container to house the Wistia snippet
                        wistiaContainer.innerHTML = wistiaSnippet;
                        wistiaPlaceholder.replaceWith(wistiaContainer); // replace with the created div
                        const scripts = wistiaContainer.querySelectorAll('script');
                        scripts.forEach(script => {
                            const newScript = document.createElement('script');
                            newScript.src = script.src;
                            newScript.async = true;
                            document.body.appendChild(newScript);
                        });
                        enableWistiaVideoCustomization();
                        observer.unobserve(entry.target); // stop observing after load
                    }
                });
            },
            {
                rootMargin: '300px 0px', // start loading when the element is 300px below the viewport
                threshold: 0.1, // trigger when 1% of the element is visible
            }
        );

        wistiaPlaceholder && observer.observe(wistiaPlaceholder);
    });

    function enableWistiaVideoCustomization() {
        window._wq = window._wq || [];
        window._wq.push({
            id: '_all',
            options: {
                videoFoam: true,
                playerColor: getComputedStyle(document.documentElement).getPropertyValue(
                    '--color--darkmid'
                ),
                controlsVisibleOnLoad: false,
                googleAnalytics: true,
                fitStrategy: 'cover',
                preload: 'none',
                wmode: 'transparent',
            },

            onReady: function (video) {
                const videoContainer = video.container.closest('.video-embed-wrapper');
                const customPlayButton = videoContainer?.querySelector(
                    '[data-custom-play-button]'
                ) as HTMLElement;
                const isPopover = video.options.popover;

                if (video.options.playButton === false) {
                    // if video from Wistia does not have a play button - hide the custom play button
                    customPlayButton && (customPlayButton.style.display = 'none');
                } else {
                    if (customPlayButton) {
                        customPlayButton.classList.remove('tw:hidden');
                        customPlayButton.style.display = 'grid';
                    }
                }

                if (customPlayButton) {
                    customPlayButton.addEventListener('click', function (e) {
                        e.preventDefault();
                        if (isPopover) {
                            video.popover.show();
                        }
                        video.play();
                    });
                }

                if (!isPopover) {
                    /* Checking for isPopover because we want to hide the button on play only if it is not a popover video */
                    video.bind('play', function () {
                        // hides the button on play
                        customPlayButton && (customPlayButton.style.display = 'none');
                    });
                    video.bind('end', function () {
                        // shows the button on video end
                        customPlayButton && (customPlayButton.style.display = 'grid');
                    });
                }
            },
        });
    }
}
